import React, {useEffect, useState} from 'react';
import {useAuth} from '../auth/AuthContext';
import {listSyncTargets, updateSyncTargetEnabled, deleteSyncTarget} from '../api/dataApi';
import {SyncSource, SyncTarget} from '../types';
import {Alert, Box, Fab, LinearProgress, Snackbar, Tooltip} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DataTableWithControls from '../components/DataTableWithControls';
import CreateSyncTargetDialog from '../components/CreateSyncTargetDialog';
import EditSyncTargetDialog from '../components/EditSyncTargetDialog';
import PreviewMetricsDialog from '../components/PreviewMetricsDialog';

export default function DataPage() {
    useAuth();
    const [items, setItems] = useState<SyncTarget[]>([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [updating, setUpdating] = useState<Record<string, boolean>>({});
    const [nameFilter, setNameFilter] = useState<string>('');
    const [snack, setSnack] = useState<{open: boolean; message: string; severity: 'success'|'error'}>({open: false, message: '', severity: 'success'});
    const [sourceFilter, setSourceFilter] = useState<'' | SyncSource>('');
    const [enabledFilter, setEnabledFilter] = useState<'all' | 'enabled' | 'disabled'>('all');

    // Edit dialog state
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState<SyncTarget | null>(null);

    // Preview dialog state
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewItem, setPreviewItem] = useState<SyncTarget | null>(null);

    async function loadData(p = page, s = size, name?: string, source?: SyncSource | '', enabledState?: 'all' | 'enabled' | 'disabled') {
        setLoading(true);
        try {
            const enabled = enabledState === 'all' ? undefined : (enabledState === 'enabled');
            const data = await listSyncTargets(p, s, name, source || undefined, enabled);
            setItems(data.content);
            setPage(data.number);
            setSize(data.size);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
        } catch (err: any) {
            // 401 is handled globally by axios interceptor (redirects to /login)
            setSnack({ open: true, message: 'Failed to load sync targets', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadData(0, size, nameFilter.trim() || undefined, sourceFilter, enabledFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounce filter changes
    useEffect(() => {
        const h = setTimeout(() => {
            loadData(0, size, nameFilter.trim() || undefined, sourceFilter, enabledFilter);
        }, 300);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nameFilter, sourceFilter, enabledFilter]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 'calc(100dvw - 100px)', boxSizing: 'border-box', mx: 'auto', px: 2 }}>
            {loading && <LinearProgress />}


            <DataTableWithControls
                items={items}
                loading={loading}
                page={page}
                size={size}
                totalElements={totalElements}
                totalPages={totalPages}
                nameFilter={nameFilter}
                sourceFilter={sourceFilter}
                enabledFilter={enabledFilter}
                updating={updating}
                onChangeFilters={(f) => {
                    if (f.name !== undefined) setNameFilter(f.name);
                    if (f.source !== undefined) setSourceFilter(f.source as any);
                    if (f.enabled !== undefined) setEnabledFilter(f.enabled as any);
                }}
                onChangePage={(newPage) => loadData(newPage, size, nameFilter.trim() || undefined, sourceFilter, enabledFilter)}
                onChangePageSize={(newSize) => {
                    setSize(newSize);
                    loadData(0, newSize, nameFilter.trim() || undefined, sourceFilter, enabledFilter);
                }}
                onToggleEnabled={async (it, newVal) => {
                    setUpdating((u) => ({ ...u, [it.id]: true }));
                    const prev = items;
                    setItems((list) => list.map((x) => x.id === it.id ? { ...x, enabled: newVal } : x));
                    try {
                        await updateSyncTargetEnabled(it.id, newVal);
                        setSnack({ open: true, message: `Enabled ${newVal ? 'on' : 'off'}`, severity: 'success' });
                        await loadData(page, size, nameFilter.trim() || undefined, sourceFilter, enabledFilter);
                    } catch (err) {
                        setItems(prev);
                        setSnack({ open: true, message: 'Failed to update enabled state', severity: 'error' });
                    } finally {
                        setUpdating((u) => {
                            const { [it.id]: _, ...rest } = u;
                            return rest;
                        });
                    }
                }}
                onRowDoubleClick={(it) => {
                    setEditItem(it);
                    setEditOpen(true);
                }}
                onPreview={(it) => {
                    setPreviewItem(it);
                    setPreviewOpen(true);
                }}
                onDelete={async (it) => {
                    const ok = window.confirm(`Delete sync target "${it.name}"? This will also delete its metrics.`);
                    if (!ok) return;
                    try {
                        await deleteSyncTarget(it.id);
                        setSnack({ open: true, message: 'Deleted sync target', severity: 'success' });
                        await loadData(0, size, nameFilter.trim() || undefined, sourceFilter, enabledFilter);
                    } catch (err) {
                        setSnack({ open: true, message: 'Failed to delete sync target', severity: 'error' });
                    }
                }}
            />

            <CreateSyncTargetDialog
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onCreated={() => loadData(0, size, nameFilter.trim() || undefined, sourceFilter, enabledFilter)}
                showSnack={setSnack}
            />

            <EditSyncTargetDialog
                open={editOpen}
                item={editItem}
                onClose={() => { setEditOpen(false); setEditItem(null); }}
                onUpdated={() => loadData(page, size, nameFilter.trim() || undefined, sourceFilter, enabledFilter)}
                showSnack={setSnack}
            />

            {/* Floating Action Button */}
            <Tooltip title="Create Sync Target">
                <Fab color="primary" aria-label="add" onClick={() => setOpenCreate(true)} sx={{ position: 'fixed', bottom: 24, right: 24 }}>
                    <AddIcon />
                </Fab>
            </Tooltip>

            <PreviewMetricsDialog
                open={previewOpen}
                target={previewItem}
                onClose={() => { setPreviewOpen(false); setPreviewItem(null); }}
            />

            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({...s, open: false}))} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(s => ({...s, open: false}))} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}